// «Estado de la empresa» on the page (shared/API.md §43): the decoded answer, the 60 s poll while the
// tab is visible, and the minute tick that moves «Sin registro» past 09:30 and «debía regresar».
// The stored answer paints the first frame (local-first) when it is today's in Core's zone.
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { data } from '../../../bridge/client';
import { ATTENDANCE_POLL_MS, Attendance, type AttendanceFeed, type AttendanceNow, type AttendanceRing } from '../../../core/attendance';
import { useLive } from '../../hooks';

let stopped = false;
let timer: ReturnType<typeof setInterval> | null = null;
let users = 0;
let inFlight = false;

async function refresh(): Promise<void> {
  if (stopped || inFlight) return;
  inFlight = true;
  try {
    const result = await data.call('attendanceRefresh');
    if (result === 'unauthorized' || result === 'unavailable') stopped = true;
  } catch {
    /* the last answer stands */
  } finally {
    inFlight = false;
  }
}

function onVisibility(): void {
  if (document.visibilityState === 'visible') void refresh();
}

// The decoded answer, published once for every reader (one worker read per change, not per avatar).
let decoded: AttendanceFeed | null = null;
const feedListeners = new Set<() => void>();

function subscribeFeed(listener: () => void): () => void {
  feedListeners.add(listener);
  return () => feedListeners.delete(listener);
}

/** Mounted once by the workspace: reads the stored answer, fetches now, every 60 s while visible, and on coming back. */
export function useAttendancePoller(): void {
  const raw = useLive(() => data.call('attendanceFeed'), ['syncState'], []).value ?? null;
  useEffect(() => {
    const next = raw ? Attendance.decode(raw) : null;
    decoded = next;
    for (const l of feedListeners) l();
  }, [raw]);
  useEffect(() => {
    users++;
    if (users === 1) {
      void refresh();
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') void refresh();
      }, ATTENDANCE_POLL_MS);
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      users--;
      if (users === 0) {
        if (timer) clearInterval(timer);
        timer = null;
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, []);
}

export function refreshAttendance(): void {
  void refresh();
}

// One minute clock for every consumer (re-renders rings at most once a minute).
let minute = Math.floor(Date.now() / 60_000);
const tickListeners = new Set<() => void>();
let tickTimer: ReturnType<typeof setInterval> | null = null;

function subscribeTick(listener: () => void): () => void {
  tickListeners.add(listener);
  if (!tickTimer) {
    tickTimer = setInterval(() => {
      const m = Math.floor(Date.now() / 60_000);
      if (m === minute) return;
      minute = m;
      for (const l of tickListeners) l();
    }, 5_000);
  }
  return () => {
    tickListeners.delete(listener);
    if (!tickListeners.size && tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  };
}

function useMinute(): number {
  return useSyncExternalStore(subscribeTick, () => minute);
}

export interface AttendanceView {
  feed: AttendanceFeed | null;
  now: AttendanceNow;
}

// "Now" in Core's zone, computed once per minute for every avatar.
let nowCache: { key: string; now: AttendanceNow } | null = null;
function nowFor(tz: string, m: number): AttendanceNow {
  const key = `${tz}|${m}`;
  if (nowCache?.key !== key) nowCache = { key, now: Attendance.now(tz, new Date(m * 60_000)) };
  return nowCache.now;
}

/** Today's answer (null when there is none for today) and "now" in Core's zone. */
export function useAttendance(): AttendanceView {
  const feed = useSyncExternalStore(subscribeFeed, () => decoded);
  const m = useMinute();
  return useMemo(() => {
    const now = nowFor(feed?.tz ?? 'America/Mexico_City', m);
    return { feed: Attendance.isCurrent(feed, now) ? feed : null, now };
  }, [feed, m]);
}

/** The ring for one person's avatar (null = none). */
export function useAttendanceRing(userId: string | null | undefined): { ring: AttendanceRing | null; label: string | null } {
  const { feed, now } = useAttendance();
  return useMemo(() => {
    const person = userId && feed ? feed.people[userId] : undefined;
    const ring = feed ? Attendance.ring(person, feed.workday, now.minutes) : null;
    return { ring, label: ring && person ? Attendance.line(person, now.minutes) : null };
  }, [feed, now, userId]);
}
